(() => {
  if (customElements.get('bythlon-pedal-cycle')) return;
  class BythlonPedalCycle extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const find = key => this.querySelector(`[data-cycle-${key}]`);
      this.values = JSON.parse(find('values').textContent);
      this.nodes = Object.fromEntries(['arm', 'back', 'back-pedal', 'pedal', 'pivot', 'phase', 'angle', 'range', 'play', 'play-icon', 'play-label', 'wheel', 'trace', 'cursor', 'dot', 'polar-dot', 'explanation'].map(key => [key, find(key)]));
      this.angle = 90;
      this.running = false;
      this.frame = null;
      this.lastTime = null;
      this.tabs = [...this.querySelectorAll('[data-cycle-tab]')];
      this.panels = [...this.querySelectorAll('[data-cycle-panel]')];
      this.querySelectorAll('[data-cycle-controls], [data-cycle-tabs]').forEach(node => { node.hidden = false; });
      this.panels.forEach(panel => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', `cycle-tab-${panel.dataset.cyclePanel}`);
        panel.tabIndex = 0;
      });
      this.selectTab('elite');
      this.nodes.range.addEventListener('input', event => {
        this.stop();
        this.render(Number(event.target.value));
      }, {signal});
      this.nodes.play.addEventListener('click', () => this.running ? this.stop() : this.play(), {signal});
      this.querySelectorAll('[data-cycle-jump]').forEach(button => button.addEventListener('click', () => {
        this.stop();
        this.render(Number(button.dataset.cycleJump));
      }, {signal}));
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.cycleTab), {signal});
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.selectTab(this.tabs[next].dataset.cycleTab);
          this.tabs[next].focus();
        }, {signal});
      });
      const revealHash = () => {
        const target = document.getElementById(location.hash.slice(1));
        const panel = target?.closest('[data-cycle-panel]');
        if (panel && this.contains(panel)) {
          this.querySelector('[data-cycle-more]').open = true;
          this.selectTab(panel.dataset.cyclePanel);
          target.scrollIntoView({block: 'center'});
        }
      };
      window.addEventListener('hashchange', revealHash, {signal});
      revealHash();
      this.bindScrub(this.nodes.trace, 'trace', signal);
      this.bindScrub(this.nodes.wheel, 'wheel', signal);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); }, {signal});
      this.observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) this.stop();
      });
      this.observer.observe(this);
      this.dataset.ready = '';
      this.render(90);
    }
    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }
    selectTab(id) {
      this.tabs.forEach(tab => {
        const selected = tab.dataset.cycleTab === id;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.cyclePanel !== id; });
    }
    bindScrub(svg, mode, signal) {
      let active = null;
      const update = event => {
        const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(svg.getScreenCTM().inverse());
        const angle = mode === 'trace' ? Math.max(0, Math.min(360, (point.x - 40) / 1.25)) : (Math.atan2(point.x - 170, 170 - point.y) * 180 / Math.PI + 360) % 360;
        this.render(angle);
      };
      svg.addEventListener('pointerdown', event => {
        if (event.button !== 0) return;
        this.stop();
        active = event.pointerId;
        svg.setPointerCapture(active);
        update(event);
      }, {signal});
      svg.addEventListener('pointermove', event => { if (event.pointerId === active) update(event); }, {signal});
      const release = () => { active = null; };
      svg.addEventListener('pointerup', release, {signal});
      svg.addEventListener('pointercancel', release, {signal});
      svg.addEventListener('lostpointercapture', release, {signal});
    }
    render(angle) {
      this.angle = Math.max(0, Math.min(360, angle));
      const a = this.angle * Math.PI / 180;
      const x = 170 + 98 * Math.sin(a), y = 170 - 98 * Math.cos(a);
      const n = this.nodes;
      n.arm.setAttribute('x2', x); n.arm.setAttribute('y2', y);
      n.back.setAttribute('x2', 340 - x); n.back.setAttribute('y2', 340 - y);
      n['back-pedal'].setAttribute('x', 326 - x); n['back-pedal'].setAttribute('y', 334 - y);
      n.pedal.setAttribute('transform', `translate(${x} ${y})`);
      n.pivot.setAttribute('cx', x); n.pivot.setAttribute('cy', y);
      const index = Math.floor(this.angle), fraction = this.angle - index;
      const torque = this.values[index] + (this.values[Math.min(index + 1, 360)] - this.values[index]) * fraction;
      const chartX = 40 + this.angle * 1.25;
      n.cursor.setAttribute('x1', chartX); n.cursor.setAttribute('x2', chartX);
      n.dot.setAttribute('cx', chartX); n.dot.setAttribute('cy', 194 - torque * 144);
      n['polar-dot'].setAttribute('cx', 170 + (98 + torque * 52) * Math.sin(a));
      n['polar-dot'].setAttribute('cy', 170 - (98 + torque * 52) * Math.cos(a));
      const color = torque < 0 ? '#3c3c3b' : 'var(--science-red)';
      n.dot.style.fill = color; n['polar-dot'].style.fill = color;
      n.angle.textContent = `${Math.round(this.angle)}°`;
      n.range.value = Math.round(this.angle);
      let phase, explanation;
      if (torque < -0.005) {
        phase = this.dataset.negative; explanation = this.dataset.explainUp;
      } else if (this.angle > 180 && torque > 0.005) {
        phase = this.dataset.positive; explanation = this.dataset.explainPositive;
      } else if (torque > 0.005) {
        phase = this.dataset.down; explanation = this.dataset.explainDown;
      } else {
        phase = this.angle < 180 ? this.dataset.top : this.dataset.bottom;
        explanation = this.dataset.explainTransition;
      }
      n.phase.textContent = phase;
      n.explanation.textContent = explanation;
      n.range.setAttribute('aria-valuetext', `${Math.round(this.angle)}°, ${phase}`);
    }
    play() {
      this.running = true;
      this.lastTime = null;
      this.nodes.play.setAttribute('aria-pressed', 'true');
      this.nodes['play-label'].textContent = this.dataset.pause;
      this.nodes['play-icon'].textContent = 'Ⅱ';
      const tick = time => {
        if (!this.running) return;
        const elapsed = this.lastTime === null ? 0 : Math.min(time - this.lastTime, 80);
        this.lastTime = time;
        this.render((this.angle + elapsed * 360 / 6500) % 360);
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    stop() {
      this.running = false;
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (!this.nodes) return;
      this.nodes.play.setAttribute('aria-pressed', 'false');
      this.nodes['play-label'].textContent = this.dataset.play;
      this.nodes['play-icon'].textContent = '▶';
    }
  }
  customElements.define('bythlon-pedal-cycle', BythlonPedalCycle);
})();
